import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { LoadingTile, CoreData } from '@carvana/merch-tile';
import { useContext, useMemo } from 'react';
import VehicleTile from '@/home/VehicleTile';
import TrackWrapper from '~/components/common/TrackWrapper';
import AvailabilityContext from '~/context/home/AvailabilityContext';
import SuggestedForYouContext from '~/context/home/SuggestedForYouContext';
import useCarouselSettings from '~/hooks/home/useCarouselSettings';
import { SuggestedVehicle } from '~/services/home/contracts/SuggestedVehiclesData';
import Carousel, {
  BottomPaddingForBoxShadow,
  CarouselSkeleton
} from '../Carousel';
import useIsInReturnUserExperiment from '../hooks/useIsInReturnUserExperiment';

function useFormatSuggestedVehicleData(
  vehicles: SuggestedVehicle[]
): CoreData[] {
  return useMemo(
    () =>
      vehicles.map(vehicle => {
        const { price, vehicleInventoryTypeId } = vehicle;

        return {
          ...vehicle,
          price: {
            ...price,
            discounts: [] //TODO If tradeIncentive ever comes back, we  will need to get the list of discounts since its not a part of the SuggestedVehicle type
          },
          transportCost: price.transportCost,
          vehicleInventoryType: vehicleInventoryTypeId
        };
      }),
    [vehicles]
  );
}

export default function SuggestedForYou() {
  const {
    suggestedVehicles: {
      hasError: suggestedVehiclesError,
      data: suggestedVehicles,
      isLoading
    }
  } = useContext(SuggestedForYouContext);
  const {
    pageAvailability: { data: availabilityData }
  } = useContext(AvailabilityContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const formattedData = useFormatSuggestedVehicleData(suggestedVehicles);
  const isInReturnUserExperiment = useIsInReturnUserExperiment();

  const subHeading = isInReturnUserExperiment
    ? undefined
    : 'Based on recently viewed vehicles';

  const { values, carouselRef } = useCarouselSettings({
    heading: 'Suggested for you',
    subHeading,
    maxLength: suggestedVehicles?.length
  });

  if (isLoading)
    return <CarouselSkeleton includeSubHeader Tile={LoadingTile} />;

  const shouldHideBasedOnCount = isInReturnUserExperiment
    ? suggestedVehicles.length < 4
    : suggestedVehicles.length < 6;

  if (suggestedVehiclesError || shouldHideBasedOnCount) return null;

  const viewEvent = {
    eventName: 'Viewed - Home feed section',
    customAttributes: {
      description: 'Suggested for you',
      isCarousel: true,
      content: {
        vehicleIds: suggestedVehicles.map(vehicle => vehicle.vehicleId)
      }
    }
  };

  function sendClickEvent(vehicleId: number) {
    trackEvent('Clicked - Home feed card', {
      description: 'Suggested for you tile',
      vehicleId
    });
  }

  return (
    <TrackWrapper event={viewEvent}>
      <Carousel {...values} ref={carouselRef} dataQa="suggested-for-you">
        {formattedData.map(vehicle => {
          const { vehicleId } = vehicle;

          const tileAvailability = availabilityData[vehicleId];

          return (
            <BottomPaddingForBoxShadow key={vehicleId}>
              <VehicleTile
                availabilityInfo={{
                  pickup: tileAvailability?.soonestPickup?.content ?? undefined,
                  delivery:
                    tileAvailability?.soonestDelivery?.content ?? undefined
                }}
                vehicle={vehicle}
                sectionTitle="Suggested for you"
                handleTileClick={() => sendClickEvent(vehicleId)}
              />
            </BottomPaddingForBoxShadow>
          );
        })}
      </Carousel>
    </TrackWrapper>
  );
}
